import type { RegisteredComponent } from "@builder.io/sdk-react";
import { isEditing, isPreviewing } from "@builder.io/sdk-react";

import { TpaBalance } from "~/hec/components/tpa-balance/tpa-balance";

import {
  mergeMockDataWithDefaults,
  tpaBalanceMockData,
  trackerMockDataSubFields,
} from "./utils";

interface BuilderTpaBalanceProps {
  balancePopupText?: string;
  useMockData?: boolean;
  mockData?: typeof tpaBalanceMockData;
  bdLogo?: { src?: string; altText?: string };
  mobileOnly?: boolean;
  hideExpiryMessages?: boolean;
  attributes?: Record<string, unknown>;
  className?: string;
}

const BuilderTpaBalance = ({
  balancePopupText = "",
  useMockData,
  mockData,
  bdLogo,
  mobileOnly,
  hideExpiryMessages,
  attributes,
}: BuilderTpaBalanceProps) => {
  const { className, ...rest } = (attributes ?? {}) as {
    className?: string;
    [key: string]: unknown;
  };

  return (
    <TpaBalance
      balancePopupText={balancePopupText}
      useMockData={useMockData}
      mockData={{ balance: mergeMockDataWithDefaults(mockData) }}
      isEdit={isEditing()}
      isPreview={isPreviewing()}
      bdLogo={bdLogo}
      mobileOnly={mobileOnly}
      hideExpiryMessages={hideExpiryMessages}
      className={className}
      {...rest}
      data-testid="tpaBalance-TpaBalance"
    />
  );
};

export const TpaBalanceDefinition = {
  component: BuilderTpaBalance,
  name: "TPABalance",
  noWrap: true,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/credit-card.svg",
  inputs: [
    {
      name: "balancePopupText",
      type: "longText",
      required: true,
      defaultValue:
        "All of your accounts are now in one place with BalanceDisplay™. The easier way to keep track of your balances and end of year purchases.",
    },
    {
      name: "useMockData",
      type: "boolean",
      required: true,
      defaultValue: false,
    },
    {
      name: "bdLogo",
      type: "object",
      required: false,
      subFields: [
        {
          name: "src",
          type: "file",
          allowedFileTypes: ["png", "jpg", "jpeg", "svg"],
          required: false,
        },
        { name: "altText", type: "text", required: false },
      ],
    },
    {
      // Hide on tablet/desktop. Set true for the account-popover placement so
      // it doesn't double-render with the below-header banner.
      name: "mobileOnly",
      type: "boolean",
      required: false,
      defaultValue: false,
    },
    {
      // Suppress the "*FSA: Spend $X by DATE" deadline messages under the pills.
      name: "hideExpiryMessages",
      type: "boolean",
      required: false,
      defaultValue: false,
    },
    {
      name: "mockData",
      type: "object",
      showIf: `options.get('useMockData') === true`,
      defaultValue: tpaBalanceMockData,
      subFields: [
        {
          name: "balance",
          type: "object",
          required: true,
          subFields: trackerMockDataSubFields,
        },
      ],
    },
  ],
} satisfies RegisteredComponent;

export default BuilderTpaBalance;
